import { Injectable } from '@angular/core';
import { CustomStateDto, WorkflowItemData, WorkflowStatus } from '@api/projects/interfaces';
import { MessplatzService } from '@app/services/messplatz/messplatz.service';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { PermissionService } from '@app/services/permission/permission.service';
import { ProjectService } from '@app/services/project/project.service';
import { ProjectRestService } from '@app/services/rest/project-rest.service';
import { WorkflowRestService } from '@app/services/rest/workflow-rest.service';
import { repeatOn } from '@app/util/operators';
import { firstValueFrom } from '@app/util/rxjs-7-compat';
import { SpecialQuestionService } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { CustomStateTransition, IQuestionnaireGroup, IQuestionnaireWorkflowItem, IWorkflow, IWorkflowItem } from '@interfaces/interfaces';
import { entries, groupBy } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { InteractiveLoginService } from '@app/services/interactive-login/interactive-login.service';
import { LoginDto } from '@api/auth/interfaces';

export interface WorkflowCategory {
	name: string;
	items: IWorkflowItem[];
}


@Injectable({
	providedIn: 'root',
})
export class WorkflowService {
	private readonly _changed$ = new BehaviorSubject(undefined);
	private readonly _activeProjectId$: Observable<string>;
	private readonly _activeParticipantId$: Observable<string | undefined>;
	
	public readonly workflow$: Observable<IWorkflow | null>;
	public readonly workflowStatus$: Observable<WorkflowStatus | null>;
	public readonly workflowCategories$: Observable<WorkflowCategory[]>;
	
	constructor(
		permissionService: PermissionService,
		projectService: ProjectService,
		projectRestService: ProjectRestService,
		activeParticipantService: ActiveParticipantService,
		private readonly workflowRestService: WorkflowRestService,
		private readonly messplatzService: MessplatzService,
		private readonly specialQuestionService: SpecialQuestionService,
		private readonly loginService: InteractiveLoginService,
	) {
		this._activeProjectId$ = projectService.activeProject$.pipe(
			permissionService.checkPermission('workflow-config:read'),
			map(({ project_id }) => project_id),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
		this._activeParticipantId$ = activeParticipantService.participant$.pipe(
			map((participant) => participant?.id),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
		
		this.workflow$ = this._activeProjectId$.pipe(
			switchMap((project_id) => project_id ? projectRestService.getWorkflow(project_id) : of<null>(null)),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
		
		this.workflowStatus$ = combineLatest([
			this._activeProjectId$,
			this._activeParticipantId$,
		]).pipe(
			repeatOn(this._changed$),
			switchMap(([project_id, id]) => id ? workflowRestService.getWorkflowStatus$(project_id, id) : of<null>(null)),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
		
		this.workflowCategories$ = this.workflow$.pipe(
			map((workflow) => entries(groupBy(workflow?.items ?? [], 'category')).map<WorkflowCategory>(([name, items]) => ({ name, items }))),
		);
	}
	
	public changed(): void {
		this._changed$.next(undefined);
	}
	
	async finishWorkflow<D = unknown>(participantId: string, itemId: string, data: D): Promise<WorkflowItemData<D>> {
		try {
			const result = await this.workflowRestService.finishWorkflowItem(
				await firstValueFrom(this._activeProjectId$),
				participantId,
				itemId,
				{
					pid: participantId,
					itemid: itemId,
					data: data,
				});
			
			this.changed();
			
			return result;
		} catch (error) {
			console.error(error);
			throw error;
		}
	}
	
	async switchToState(transition: CustomStateTransition, participantId: string, itemId: string): Promise<string | null> {
		return await this.bulkSwitchToState$(transition, [participantId], [itemId]);
	}

	async bulkSwitchToState$(transition: CustomStateTransition, participantIds: string[], itemIds: string[]): Promise<string | null> {
		let login: LoginDto | undefined = undefined;
		
		if (transition.requiresAuthentication) {
			login = (await this.loginService.promptLogin()) ?? undefined;
			
			if (!login){
				// canceled
				return null;
			}
		}
		
		const result = await this.workflowRestService.bulkSwitchToState$(await firstValueFrom(this._activeProjectId$), transition.endStateId, { 
			itemids: itemIds,
			participantids: participantIds,
			login
		}).toPromise();
		
		this.changed();
		
		return result;
	}
	
	async rerunItemTriggers(participantId: string, itemId: string): Promise<WorkflowItemData> {
		const result = await this.workflowRestService.resetWorkflowItemData$(await firstValueFrom(this._activeProjectId$), participantId, itemId);
		
		this.changed();
		
		return result;
	}
	
	public hasAllDevicesByType(type: string): boolean {
		if (this.specialQuestionService.isSpecialQuestion(type)) {
			const specialQuestion = this.specialQuestionService.getSpecialQuestionByType(type);
			return this.messplatzService.hasAllDevices(specialQuestion.requiredDevices);
		}
		return true;
	}
	
	public hasAllDevicesbyQuestionnaire(questionnaire: IQuestionnaireWorkflowItem): boolean {
		return questionnaire?.groups?.every(group => this.hasAllDevicesByPage(group)) ?? true;
	}
	
	public hasAllDevicesByPage(page: IQuestionnaireGroup): boolean {
		return page.items.every(item => this.hasAllDevicesByType(item.type));
	}
	
	public async resetWorkflowItemStatus(itemId: string): Promise<void> {
		const workflowStatus = await firstValueFrom(this.workflowStatus$);
		const [
			projectId,
			participantId,
		] = await Promise.all([
			firstValueFrom(this._activeProjectId$),
			firstValueFrom(this._activeParticipantId$),
		]);
		
		if (!workflowStatus || !participantId) {
			return;
		}
		
		if (workflowStatus.status[itemId].completed) {
			await this.workflowRestService.deleteWorkflowItemData$(projectId, participantId, itemId);
		} else {
			await this.workflowRestService.deleteStatus(projectId, participantId, itemId);
		}
		
		this.changed();
	}
}
